import { ref } from 'vue'
import type { Ref } from 'vue'
import { updateAccountBill, batchUpdateAccountBill, type AccountBillItem } from '@/api/billApi'
import { message } from 'ant-design-vue'
import { OK } from '@/common'

export const createEmptyBillRecord = (): AccountBillItem => ({
  id: '',
  pay_chnl: '',
  dateTime: '',
  accountId: 0,
  accountName: '',
  currency: '',
  collectionSubject: '',
  receivingBank: '',
  bankCardFourDigits: '',
  estimatedRevenue: '',
  expectedReceiptTime: '',
  actualInvoiceAmount: '',
  actualReceivedAmount: '',
  actualReceiptTime: '',
  receivedStatus: null,
  receivedResCode: null,
  receivedResMsg: null,
  createTime: '',
  updateTime: '',
  isNeedExternalSettlement: 0,
  balance: '',
  expectReceivedAmount: '',
  receivedDiff: '',
  receivedResExtInfo: {
    imageList: [],
    receivedResList: [],
  },
  withholdingTax: '',
})

type EmitType = {
  (event: 'update-list'): void
}

export const useOtherReason = ({
  emit,
  isBatchOperation,
  selectedRowKeys,
}: {
  emit: EmitType
  isBatchOperation: Ref<boolean>
  selectedRowKeys: Ref<string[]>
}) => {
  const otherReasonModalVisible = ref(false)
  const currentRecord = ref<AccountBillItem>(createEmptyBillRecord())
  const originalRecord = ref<AccountBillItem>(createEmptyBillRecord())
  const fileList = ref<any[]>([])
  const previewVisible = ref(false)
  const previewImage = ref('')
  const previewTitle = ref('')
  const textareaRef = ref<any>(null)

  const handleEditCustomReason = (record: AccountBillItem) => {
    currentRecord.value = {
      ...record,
      receivedResExtInfo: {
        imageList: record.receivedResExtInfo?.imageList || [],
        receivedResList:
          record.receivedResExtInfo?.receivedResList?.map((item) => ({
            ...item,
            receivedResCode: item.receivedResCode?.toString(),
          })) || [],
        supplementRemark: record.receivedResExtInfo?.supplementRemark || '',
      },
    }
    originalRecord.value = {
      ...record,
      receivedResExtInfo: {
        imageList: record.receivedResExtInfo?.imageList || [],
        receivedResList: record.receivedResExtInfo?.receivedResList || [],
      },
    }
    fileList.value = (record.receivedResExtInfo?.imageList || []).map((url, index) => ({
      uid: `-${index}`,
      name: url.split('/').pop() || `image-${index}`,
      status: 'done',
      url: url,
    }))
    otherReasonModalVisible.value = true
  }

  const handleOtherReasonConfirm = async ({
    currentRecord: rec,
    fileUrls,
  }: {
    currentRecord: AccountBillItem
    fileUrls: string[]
  }) => {
    if (
      rec.receivedResExtInfo?.receivedResList?.some(
        (item) => String(item.receivedResCode) === '60' && !item.receivedResMsg,
      )
    ) {
      return message.error('请输入异常原因')
    }
    try {
      if (isBatchOperation.value && selectedRowKeys.value.length > 0) {
        const res = await batchUpdateAccountBill({
          ids: selectedRowKeys.value,
          receivedResExtInfo: {
            imageList: fileUrls,
            receivedResList: rec.receivedResExtInfo?.receivedResList || [],
            supplementRemark: rec.receivedResExtInfo?.supplementRemark || '',
          },
        })
        if (res && res.code === OK) {
          message.success(`成功更新${selectedRowKeys.value.length}条记录`)
          emit('update-list')
          otherReasonModalVisible.value = false
          selectedRowKeys.value = []
          isBatchOperation.value = false
        }
      } else {
        const res = await updateAccountBill({
          id: rec.id,
          receivedResExtInfo: {
            imageList: fileUrls,
            receivedResList: rec.receivedResExtInfo?.receivedResList || [],
            supplementRemark: rec.receivedResExtInfo?.supplementRemark || '',
          },
        })
        if (res && res.code === OK) {
          message.success('更新成功')
          emit('update-list')
          otherReasonModalVisible.value = false
        }
      }
    } catch (error) {
      console.log('[ error ] >', error)
    }
  }

  const handleOtherReasonCancel = () => {
    otherReasonModalVisible.value = false
    if (isBatchOperation.value) {
      isBatchOperation.value = false
    }
  }

  return {
    otherReasonModalVisible,
    currentRecord,
    originalRecord,
    fileList,
    previewVisible,
    previewImage,
    previewTitle,
    textareaRef,
    handleEditCustomReason,
    handleOtherReasonConfirm,
    handleOtherReasonCancel,
  }
}
